import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { MdClose } from 'react-icons/md';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PeoplePicker,
  Tooltip,
  TOOLTIP_PLACEMENT_TOP,
  ConfirmDialog,
  CONFIRM_DIALOG_TONE_INFO,
  type PeoplePickerPerson,
} from '@aibuild/ui';
import useSnackbar from '@hooks/useSnackbar';
import PageHeader, {
  PAGE_HEADER_VARIANT_SMALL,
} from '@components/2-molecules/PageHeader';
import IconButton, {
  ICON_BUTTON_VARIANT_PRIMARY,
  ICON_BUTTON_VARIANT_STANDARD,
} from '@components/1-atoms/IconButton';
import {
  listGroups,
  createGroup,
  renameGroup,
  deleteGroup,
  listGroupMembers,
  addGroupMembers,
  removeGroupMember,
  type GroupResponse,
} from '@api/groups';
import type { MemberResponse } from '@api/organizations';
import { Frame } from '../Settings.styled';
import { Input, SmallLabel, InputGroup } from './Organization.styled';
import {
  CreateRow,
  GroupList,
  GroupItem,
  GroupHeader,
  GroupNameCell,
  GroupMeta,
  GroupActions,
  ChipRow,
  MemberChip,
  ChipRemove,
  AddRow,
  PickerWrap,
  EmptyText,
  HintText,
  ActionIcon,
} from './OrgGroupsSection.styled';

export interface OrgGroupsSectionProps {
  readonly token: string;
  readonly isAdmin: boolean;
  readonly members: readonly MemberResponse[];
  readonly onChanged: () => void;
}

interface ConfirmState {
  open: boolean;
  groupId: string;
  groupName: string;
}

const CONFIRM_CLOSED: ConfirmState = { open: false, groupId: '', groupName: '' };
const GROUPS_KEY = ['organization', 'groups'] as const;
const MEMBERS_KEY = ['organization', 'group-members'] as const;

const errMsg = (err: unknown, fallback: string) =>
  err instanceof Error ? err.message : fallback;

const GroupMembersEditor: React.FC<{
  token: string;
  groupId: string;
  isAdmin: boolean;
  people: readonly PeoplePickerPerson[];
  onChanged: () => void;
}> = ({ token, groupId, isAdmin, people, onChanged }) => {
  const intl = useIntl();
  const { showError } = useSnackbar();
  const queryClient = useQueryClient();

  const { data: groupMembers = [] } = useQuery({
    queryKey: [...MEMBERS_KEY, groupId],
    queryFn: () => listGroupMembers(token, groupId),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [...MEMBERS_KEY, groupId] });
    queryClient.invalidateQueries({ queryKey: GROUPS_KEY });
    onChanged();
  };

  const addMutation = useMutation({
    mutationFn: (userIds: string[]) => addGroupMembers(token, groupId, userIds),
    onSuccess: invalidate,
    onError: (err) => showError(errMsg(err, 'Failed to add members')),
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => removeGroupMember(token, groupId, userId),
    onSuccess: invalidate,
    onError: (err) => showError(errMsg(err, 'Failed to remove member')),
  });

  const memberIdSet = useMemo(
    () => new Set(groupMembers.map((m) => m.userId)),
    [groupMembers],
  );
  const candidates = useMemo(
    () => people.filter((p) => !memberIdSet.has(p.id)),
    [people, memberIdSet],
  );

  return (
    <>
      <ChipRow data-testid={`group-members-${groupId}`}>
        {groupMembers.length === 0 ? (
          <HintText>
            {intl.formatMessage({
              id: 'org.groups.noMembers',
              defaultMessage: 'No members yet.',
            })}
          </HintText>
        ) : (
          groupMembers.map((m) => (
            <MemberChip key={m.userId} data-testid={`group-member-chip-${m.userId}`}>
              {m.displayName || m.email || m.userId}
              {isAdmin && (
                <Tooltip
                  content={intl.formatMessage({
                    id: 'org.groups.removeMember',
                    defaultMessage: 'Remove member',
                  })}
                  placement={TOOLTIP_PLACEMENT_TOP}
                  portal
                >
                  <ChipRemove
                    type="button"
                    aria-label={intl.formatMessage({
                      id: 'org.groups.removeMember',
                      defaultMessage: 'Remove member',
                    })}
                    data-testid={`group-member-remove-${m.userId}`}
                    onClick={() => removeMutation.mutate(m.userId)}
                  >
                    <MdClose />
                  </ChipRemove>
                </Tooltip>
              )}
            </MemberChip>
          ))
        )}
      </ChipRow>
      {isAdmin && (
        <AddRow>
          <PickerWrap>
            <PeoplePicker
              people={candidates}
              selectedIds={[]}
              onChange={(ids) => {
                if (ids.length > 0) addMutation.mutate(ids);
              }}
              placeholder={intl.formatMessage({
                id: 'org.groups.addPlaceholder',
                defaultMessage: 'Select members to add…',
              })}
              dataTestId={`group-add-picker-${groupId}`}
            />
          </PickerWrap>
        </AddRow>
      )}
    </>
  );
};

const GroupRow: React.FC<{
  token: string;
  group: GroupResponse;
  isAdmin: boolean;
  people: readonly PeoplePickerPerson[];
  expanded: boolean;
  onToggleExpand: () => void;
  onRenamed: () => void;
  onRequestDelete: () => void;
  onChanged: () => void;
}> = ({
  token,
  group,
  isAdmin,
  people,
  expanded,
  onToggleExpand,
  onRenamed,
  onRequestDelete,
  onChanged,
}) => {
  const intl = useIntl();
  const { showError } = useSnackbar();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(group.name);

  const renameMutation = useMutation({
    mutationFn: (nextName: string) => renameGroup(token, group.id, nextName),
    onSuccess: () => {
      setEditing(false);
      onRenamed();
    },
    onError: (err) => showError(errMsg(err, 'Failed to rename group')),
  });

  const handleRename = () => {
    const trimmed = name.trim();
    if (trimmed.length >= 2) renameMutation.mutate(trimmed);
  };

  return (
    <GroupItem data-testid={`group-item-${group.id}`}>
      <GroupHeader>
        {editing ? (
          <GroupActions>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-label={intl.formatMessage({
                id: 'org.groups.nameLabel',
                defaultMessage: 'Group name',
              })}
              data-testid={`group-rename-input-${group.id}`}
            />
            <Tooltip
              content={intl.formatMessage({ id: 'org.save', defaultMessage: 'Save' })}
              placement={TOOLTIP_PLACEMENT_TOP}
              portal
            >
              <ActionIcon>
                <IconButton
                  variant={ICON_BUTTON_VARIANT_PRIMARY}
                  iconName="done_0"
                  onClick={handleRename}
                  dataTestId={`group-rename-save-${group.id}`}
                />
              </ActionIcon>
            </Tooltip>
            <Tooltip
              content={intl.formatMessage({ id: 'org.cancel', defaultMessage: 'Cancel' })}
              placement={TOOLTIP_PLACEMENT_TOP}
              portal
            >
              <ActionIcon>
                <IconButton
                  variant={ICON_BUTTON_VARIANT_STANDARD}
                  iconName="close_0"
                  onClick={() => {
                    setEditing(false);
                    setName(group.name);
                  }}
                  dataTestId={`group-rename-cancel-${group.id}`}
                />
              </ActionIcon>
            </Tooltip>
          </GroupActions>
        ) : (
          <GroupNameCell data-testid={`group-name-${group.id}`}>
            <strong>{group.name}</strong>
            <GroupMeta>
              {intl.formatMessage(
                { id: 'org.groups.memberCount', defaultMessage: '{count} members' },
                { count: group.memberCount },
              )}
            </GroupMeta>
          </GroupNameCell>
        )}
        {!editing && (
          <GroupActions>
            <Tooltip
              content={
                expanded
                  ? intl.formatMessage({ id: 'org.groups.done', defaultMessage: 'Done' })
                  : intl.formatMessage({
                      id: 'org.groups.manage',
                      defaultMessage: 'Manage members',
                    })
              }
              placement={TOOLTIP_PLACEMENT_TOP}
              portal
            >
              <ActionIcon>
                <IconButton
                  variant={ICON_BUTTON_VARIANT_STANDARD}
                  iconName={expanded ? 'expand_less_0' : 'expand_more_0'}
                  onClick={onToggleExpand}
                  dataTestId={`group-manage-${group.id}`}
                />
              </ActionIcon>
            </Tooltip>
            {isAdmin && (
              <>
                <Tooltip
                  content={intl.formatMessage({ id: 'org.groups.rename', defaultMessage: 'Rename' })}
                  placement={TOOLTIP_PLACEMENT_TOP}
                  portal
                >
                  <ActionIcon>
                    <IconButton
                      variant={ICON_BUTTON_VARIANT_STANDARD}
                      iconName="edit_0"
                      onClick={() => setEditing(true)}
                      dataTestId={`group-rename-${group.id}`}
                    />
                  </ActionIcon>
                </Tooltip>
                <Tooltip
                  content={intl.formatMessage({ id: 'org.groups.delete', defaultMessage: 'Delete' })}
                  placement={TOOLTIP_PLACEMENT_TOP}
                  portal
                >
                  <ActionIcon>
                    <IconButton
                      variant={ICON_BUTTON_VARIANT_STANDARD}
                      iconColor="error"
                      iconName="delete_0"
                      onClick={onRequestDelete}
                      dataTestId={`group-delete-${group.id}`}
                    />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
          </GroupActions>
        )}
      </GroupHeader>
      {expanded && (
        <GroupMembersEditor
          token={token}
          groupId={group.id}
          isAdmin={isAdmin}
          people={people}
          onChanged={onChanged}
        />
      )}
    </GroupItem>
  );
};

const OrgGroupsSection: React.FC<OrgGroupsSectionProps> = ({
  token,
  isAdmin,
  members,
  onChanged,
}) => {
  const intl = useIntl();
  const { showSuccess, showError } = useSnackbar();
  const queryClient = useQueryClient();
  const [createName, setCreateName] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmState>(CONFIRM_CLOSED);

  const { data: groups = [] } = useQuery({
    queryKey: GROUPS_KEY,
    queryFn: () => listGroups(token),
  });

  const refreshGroups = () =>
    queryClient.invalidateQueries({ queryKey: GROUPS_KEY });

  const people = useMemo<PeoplePickerPerson[]>(
    () =>
      members
        .filter((m) => m.expired !== true && m.banned !== true)
        .map((m) => ({ id: m.id, name: m.displayName || m.email })),
    [members],
  );

  const createMutation = useMutation({
    mutationFn: (name: string) => createGroup(token, name),
    onSuccess: () => {
      setCreateName('');
      showSuccess(
        intl.formatMessage({ id: 'org.groups.created', defaultMessage: 'Group created' }),
      );
      refreshGroups();
    },
    onError: (err) => showError(errMsg(err, 'Failed to create group')),
  });

  const deleteMutation = useMutation({
    mutationFn: (target: { id: string; name: string }) => deleteGroup(token, target.id),
    onSuccess: (_data, target) => {
      if (expandedId === target.id) setExpandedId(null);
      showSuccess(
        intl.formatMessage(
          { id: 'org.groups.deleted', defaultMessage: 'Group "{name}" deleted' },
          { name: target.name },
        ),
      );
      refreshGroups();
      onChanged();
    },
    onError: (err) => showError(errMsg(err, 'Failed to delete group')),
  });

  const handleCreate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = createName.trim();
    if (trimmed.length >= 2) createMutation.mutate(trimmed);
  };

  const handleConfirmDelete = () => {
    deleteMutation.mutate({ id: confirm.groupId, name: confirm.groupName });
  };

  return (
    <>
      <ConfirmDialog
        open={confirm.open}
        tone={CONFIRM_DIALOG_TONE_INFO}
        title={intl.formatMessage({ id: 'org.groups.deleteTitle', defaultMessage: 'Delete group' })}
        confirmLabel={intl.formatMessage({ id: 'org.groups.delete', defaultMessage: 'Delete' })}
        description={intl.formatMessage(
          {
            id: 'org.groups.deleteMessage',
            defaultMessage:
              'Delete "{name}"? Members are removed and any process gate using this group must be reconfigured. This cannot be undone.',
          },
          { name: confirm.groupName },
        )}
        onConfirm={handleConfirmDelete}
        onClose={() => setConfirm(CONFIRM_CLOSED)}
      />

      <PageHeader
        variant={PAGE_HEADER_VARIANT_SMALL}
        title={intl.formatMessage(
          { id: 'org.groups.title', defaultMessage: 'Groups ({count})' },
          { count: groups.length },
        )}
      />
      <Frame>
        {isAdmin && (
          <CreateRow onSubmit={handleCreate}>
            <InputGroup>
              <SmallLabel>
                {intl.formatMessage({ id: 'org.groups.newLabel', defaultMessage: 'New group name' })}
              </SmallLabel>
              <Input
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder={intl.formatMessage({
                  id: 'org.groups.newPlaceholder',
                  defaultMessage: 'e.g. Reviewers',
                })}
                data-testid="group-create-input"
              />
            </InputGroup>
            <Tooltip
              content={intl.formatMessage({ id: 'org.groups.create', defaultMessage: 'Create' })}
              placement={TOOLTIP_PLACEMENT_TOP}
              portal
            >
              <ActionIcon>
                <IconButton
                  variant={ICON_BUTTON_VARIANT_PRIMARY}
                  submitType
                  iconName="add_0"
                  disabled={createMutation.isPending || createName.trim().length < 2}
                  dataTestId="group-create-submit"
                />
              </ActionIcon>
            </Tooltip>
          </CreateRow>
        )}

        {groups.length === 0 ? (
          <EmptyText data-testid="groups-empty">
            {intl.formatMessage({
              id: 'org.groups.empty',
              defaultMessage: 'No groups yet.',
            })}
          </EmptyText>
        ) : (
          <GroupList>
            {groups.map((group) => (
              <GroupRow
                key={group.id}
                token={token}
                group={group}
                isAdmin={isAdmin}
                people={people}
                expanded={expandedId === group.id}
                onToggleExpand={() =>
                  setExpandedId((cur) => (cur === group.id ? null : group.id))
                }
                onRenamed={() => {
                  refreshGroups();
                  onChanged();
                }}
                onRequestDelete={() =>
                  setConfirm({ open: true, groupId: group.id, groupName: group.name })
                }
                onChanged={() => {
                  refreshGroups();
                  onChanged();
                }}
              />
            ))}
          </GroupList>
        )}
      </Frame>
    </>
  );
};

export default OrgGroupsSection;
